import type { FilterType } from "@amzn/ads-discovery-theme-service-typescript-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";

import { getDiscoveryApiClient } from "../../api/discoveryApi/discoveryApi";
import {
    ALL_FAMILIES,
    InsightFamilyNameToLabelMap,
} from "../../constants/filters";
import {
    IHubContentProvider,
    InsightFamilyName,
    PackageSource,
} from "../../types/providers";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

export function useFilterTypesAction(): [
    AsyncState<FilterType[] | undefined>,
    UseAsyncActions<FilterType[]>,
] {
    const [asyncFilterTypesState, asyncFilterTypesAction] = useAsync(
        async () => {
            const response = await (
                await getDiscoveryApiClient()
            ).getFilterTypes();

            return response.data ?? [];
        },
    );

    useAsyncErrorStatusNotification(asyncFilterTypesState);

    return [asyncFilterTypesState, asyncFilterTypesAction];
}

export const addUatToInsightFilters = (filters: FilterType[]): FilterType[] => {
    const filter = filters.find((f) => f.contentType === "Insights");
    if (
        filter?.providers?.find(
            (p) => p.provider?.label === "UAT Standalone Insights",
        )
    ) {
        return filters;
    } else {
        filter?.providers?.push({
            provider: {
                label: "UAT Standalone Insights",
                value: IHubContentProvider.INSIGHT_FAMILIES,
            },
            scopes: [
                {
                    label: "Peer Benchmarking",
                    tag: {
                        label: "UAT",
                    },
                    value: InsightFamilyName.PEER_BENCHMARKING_UAT,
                },
                {
                    label: "Brand Loyalty",
                    tag: {
                        label: "UAT",
                    },
                    value: "BrandLoyaltyUat",
                },
                {
                    label: "Audience Funnel",
                    tag: {
                        label: "UAT",
                    },
                    value: "AudienceFunnelSizingUat",
                },
                {
                    label: "New To Brand",
                    tag: {
                        label: "UAT",
                    },
                    value: "NewToBrandUat",
                },
                {
                    label: "Repeat Purchaser",
                    tag: {
                        label: "UAT",
                    },
                    value: "RepeatPurchaserUat",
                },
            ],
        });
    }
    return filters;
};

export const addAllTypeOption = (filters: FilterType[]): FilterType[] => {
    const standaloneInsights = filters
        .find((f) => f.contentType === "Insights")
        ?.providers?.find((p) => p.provider?.label === "Standalone insights");
    if (standaloneInsights) {
        standaloneInsights.scopes?.push({
            label: InsightFamilyNameToLabelMap.ALL_FAMILIES,
            value: ALL_FAMILIES,
        });
    }
    return filters;
};

export const replaceCaireValueForPackageAPI = (
    filters: FilterType[],
): FilterType[] => {
    const caireInsights = filters
        .find((f) => f.contentType === "Insights")
        ?.providers?.find((p) => p.provider?.label === "Pre-packaged insights")
        ?.scopes?.find((e) => e.value === PackageSource.CAIRE);
    if (caireInsights) {
        caireInsights.value = "OutOfBudget|CoreFour|Keywords";
    }
    return filters;
};
